import type {ChartData} from "@mantine/charts";
import {Bar, BarChart as BarChartRecharts, CartesianGrid, Label, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import type {ChartSeries} from "~/components/reports/widgetSpecific/types";
import {graphColors} from "~/constants/graphColors";
import {graphsXAxisTickFormatter} from "~/components/reports/utilities";import {useAnankePreferences} from "@vesta/ananke/contexts/anankeClientContext";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {numberToHumanFriendlyStringWithNumberSystem} from "~/utilities/utilities";

export function StackedBarChart({
    chartData,
    chartSeries,
    xAxisKey,
    xAxisTitle,
    scalingFactor,
    height,
    maxYValue,
    maxYValueSecondary,
}: {
    chartData: ChartData;
    chartSeries: Array<ChartSeries>;
    xAxisKey: string;
    xAxisTitle: string;
    scalingFactor: number;
    height: number;
    maxYValue: number;
    maxYValueSecondary: number | null;
}) {
    const yAxisLabel = chartSeries[0]?.label;
    const {theme} = useAnankePreferences();

    return (
        <ResponsiveContainer
            width="100%"
            height={height}
        >
            <BarChartRecharts
                data={chartData}
                margin={{top: 20, right: 20, bottom: 20, left: 20}}
                barGap={"6%"}
                maxBarSize={32}
            >
                <XAxis
                    dataKey={xAxisKey}
                    scale="auto"
                    tickFormatter={(value) => graphsXAxisTickFormatter(value)}
                    tickMargin={20}
                    tickSize={0}
                    tick={{
                        fontSize: "10px",
                    }}
                    axisLine={true}
                >
                    <Label
                        value={xAxisTitle}
                        dy={28}
                        style={{
                            textAnchor: "middle",
                            fontSize: "10px",
                        }}
                    />
                </XAxis>

                <CartesianGrid
                    horizontal={true}
                    vertical={false}
                    stroke={theme === "dark" ? "#ADACAC" : "#262626"}
                    strokeDasharray="7 5"
                />

                {yAxisLabel !== null && yAxisLabel !== undefined && (
                    <YAxis
                        yAxisId={"left"}
                        orientation={"left"}
                        tickFormatter={(value) => {
                            return `${numberToHumanFriendlyStringWithNumberSystem(value, NumberSystems.indian)}`;
                        }}
                        tickSize={0}
                        tickMargin={4}
                        tick={{
                            fontSize: "10px",
                        }}
                        axisLine={false}
                        domain={[0, maxYValue]}
                    >
                        <Label
                            value={yAxisLabel}
                            position={"insideLeft"}
                            offset={0}
                            angle={-90}
                            style={{
                                textAnchor: "middle",
                                fontSize: "10px",
                            }}
                        />
                    </YAxis>
                )}

                <Tooltip
                    contentStyle={{
                        color: theme === "dark" ? "#C7C7C7" : "#292a2e",
                        background: theme === "dark" ? "#1F1F1F" : "#ffffff",
                        border: theme === "dark" ? "1px solid #383838" : "1px solid #0b120e24",
                        borderRadius: "4px",
                        fontSize: "14px",
                    }}
                    labelFormatter={(value) => {
                        return graphsXAxisTickFormatter(value);
                    }}
                    cursor={{
                        fill: theme === "dark" ? "#2B2B2B" : "#E5E5E5",
                    }}
                    formatter={(value) => {
                        const number = parseFloat(value.toString());
                        return Number.isInteger(number) === true ? number : number.toFixed(2);
                    }}
                    content={<StackedBarTooltip xAxisKey={xAxisKey} />}
                    shared={false}
                />

                <Legend
                    verticalAlign="top"
                    align="right"
                    wrapperStyle={{
                        top: "-4px",
                        fontSize: "10px",
                    }}
                />

                {chartSeries.map((series, seriesIndex) => {
                    return (
                        <Bar
                            dataKey={series.name}
                            fill={graphColors[seriesIndex % graphColors.length]}
                            key={seriesIndex}
                            yAxisId={series.yAxisId}
                            stackId="a"
                        />
                    );
                })}
            </BarChartRecharts>
        </ResponsiveContainer>
    );
}

function StackedBarTooltip(props) {
    const {payload, active, xAxisKey} = props;
    const {theme} = useAnankePreferences();
    if (active !== null && active !== undefined && payload !== null && payload !== undefined && Array.isArray(payload) === true && payload[0] !== undefined) {
        const dataKey = payload[0].dataKey;

        const metrics = structuredClone(payload[0].payload);
        delete metrics[xAxisKey];

        const totalValue = Object.values(metrics).reduce((acc, cur) => {
            if (cur === undefined || cur === null) {
                return acc;
            }
            return acc + parseFloat(cur);
        }, 0);

        return (
            <div
                className="custom-tooltip"
                style={{
                    color: theme === "dark" ? "#C7C7C7" : "#292a2e",
                    background: theme === "dark" ? "#1F1F1F" : "#ffffff",
                    border: theme === "dark" ? "1px solid #383838" : "1px solid #0b120e24",
                    borderRadius: "4px",
                    fontSize: "14px",
                    padding: "12px 12px",
                }}
            >
                <div
                    style={{
                        color: payload[0].fill,
                    }}
                >
                    {dataKey}: {payload[0].payload[dataKey]} (
                    {Number.isInteger(payload[0].payload[dataKey] / totalValue) === true
                        ? (payload[0].payload[dataKey] / totalValue) * 100
                        : ((payload[0].payload[dataKey] / totalValue) * 100).toFixed(2)}
                    %)
                </div>
                <div
                    style={{
                        color: theme === "dark" ? "#C7C7C7" : "#292a2e",
                    }}
                >
                    {props.labelFormatter(payload[0].payload[xAxisKey])}: {totalValue}
                </div>
            </div>
        );
    }

    return null;
}
